import React, { useState, useMemo, useRef, useEffect } from "react";
import styles from "../assets/scss/common/TeamsSidebar.module.scss";
import { Link, useLocation } from "react-router-dom";
import { buildNavData } from "../helpers/buildNavData";
import ProfileDropdown from "../Components/Common/ProfileDropdown";
import LogoDark from "../assets/images/favicon.png";
import { usePermission } from "../context/PermissionContext";
import { encrypt } from "common/utils/encryptDecrypt";

/* ---------- Types ---------- */

interface SubChildMenuItem {
  formname: string;
  formurl: string;
  iconclass?: string;
}

interface ChildMenuItem {
  formname: string;
  formurl: string;
  iconclass?: string;
  lstsubchildform?: SubChildMenuItem[];
}

interface NavItem {
  formname: string;
  formurl: string;
  iconclass?: string;
  lstchildform?: ChildMenuItem[];
}

interface AppModule {
  ModuleName: string;
  ModuleURL: string;
  ModuleId: number;
  IsDefault: number;
  CompModuleId: number;
  IsAssigned: number;
  ModuleLogoPath?: string;
}

//  Session / module helpers

/**
 * Reads and parses the logged-in user object from sessionStorage.
 * Returns null if it isn't present or isn't valid JSON.
 */
const getCurrentUserFromSession = (): any | null => {
  const currentUserRaw = sessionStorage.getItem("currentUser");
  if (!currentUserRaw) return null;

  try {
    return JSON.parse(currentUserRaw);
  } catch (error) {
    console.error("Error parsing currentUser from session:", error);
    return null;
  }
};

/**
 * Normalizes UserModuleList off the current user object.
 * It may already be an array, or it may be a JSON-encoded string.
 */
const extractModuleList = (currentUser: any): AppModule[] => {
  let moduleList = currentUser?.UserModuleList;

  if (typeof moduleList === "string") {
    try {
      moduleList = JSON.parse(moduleList);
    } catch (error) {
      console.error("Error parsing UserModuleList:", error);
      return [];
    }
  }

  return Array.isArray(moduleList) ? moduleList : [];
};

/**
 * Removes duplicate modules (by ModuleId) and excludes the module the
 * user is currently logged into.
 */
const dedupeAndExcludeCurrentModule = (
  moduleList: AppModule[],
  loginModuleId: string,
): AppModule[] => {
  const uniqueModules = new Map<string, AppModule>();

  moduleList.forEach((module) => {
    const id = String(module?.ModuleId);
    if (id && !uniqueModules.has(id)) {
      uniqueModules.set(id, module);
    }
  });

  return Array.from(uniqueModules.values()).filter(
    (module) => String(module.ModuleId) !== loginModuleId,
  );
};

/**
 * Builds the final list of modules available for the app launcher,
 * sourced entirely from sessionStorage (same source used by HelpMenu).
 */
const loadAvailableModules = (): AppModule[] => {
  const currentUser = getCurrentUserFromSession();
  if (!currentUser) return [];

  const moduleList = extractModuleList(currentUser);
  const loginModuleId = String(sessionStorage.getItem("loginModuleId"));

  return dedupeAndExcludeCurrentModule(moduleList, loginModuleId);
};

/**
 * Builds the query string used to SSO/hand off into another module,
 * encrypting each parameter the target module expects.
 */
const buildModuleLaunchUrl = (
  url: string,
  moduleId: any,
  isDefault: any,
  companyModuleId: any,
): string => {
  const userdata = JSON.parse(sessionStorage.getItem("currentUser") || "{}");

  const id = encrypt(userdata.userid);
  const userName = encrypt(userdata.username);
  const userPassword = encrypt(userdata.userpassword);
  const actionIP = encrypt(sessionStorage.getItem("iPAddress"));
  const originUrl = encrypt(window.location.origin);
  const loginFrom = encrypt(isDefault ? "Module" : "SSO");
  const loginModuleId = encrypt(moduleId?.toString());
  const compModuleId = encrypt(companyModuleId?.toString());

  const commonParams =
    `?id=${id}` +
    `&name=${userName}` +
    `&pwd=${userPassword}` +
    `&ip=${actionIP}` +
    `&loginFrom=${loginFrom}` +
    `&loginModuleId=${loginModuleId}` +
    `&compModuleId=${compModuleId}`;

  if (isDefault) {
    const token = encrypt(sessionStorage.getItem("token"));
    return `${url}${commonParams}&ModuleUrl=${originUrl}&currenttoken=${token}`;
  }

  return `${url}${commonParams}&SSOUrl=${originUrl}`;
};

/**
 * Opens the target module in a new tab, handling popup-blocker failures.
 */
const navigateToModule = (
  url: string,
  moduleId: any,
  isDefault: any,
  companyModuleId: any,
) => {
  if (!url) return;

  try {
    const fullUrl = buildModuleLaunchUrl(url, moduleId, isDefault, companyModuleId);
    const newTab = window.open(fullUrl, "_blank");
    if (!newTab) alert("Popup blocked. Please allow popups.");
  } catch (error) {
    console.error("Module navigation error:", error);
  }
};


const VistaSideBar: React.FC = () => {
  const rawNavData = buildNavData() as NavItem[];
  const { hasPermission, isModuleVisible } = usePermission();
  const location = useLocation();

  /* ---- Permission-based filtering (recursive) ---- */
  const filterNavData = (items: any[]): any[] => {
    return items
      .filter((item) => {
        if (item.formname && !isModuleVisible(item.formname)) return false;
        if (item.module && !hasPermission(item.module)) return false;
        return true;
      })
      .map((item) => {
        const filteredItem = { ...item };
        if (item.lstchildform) filteredItem.lstchildform = filterNavData(item.lstchildform);
        if (item.lstsubchildform) filteredItem.lstsubchildform = filterNavData(item.lstsubchildform);
        return filteredItem;
      })
      .filter((item) => {
        const hasChildren =
          (item.lstchildform && item.lstchildform.length > 0) ||
          (item.lstsubchildform && item.lstsubchildform.length > 0);
        const hasSubmenuDefinition =
          item.lstchildform !== undefined || item.lstsubchildform !== undefined;
        if (hasSubmenuDefinition && !hasChildren) return false;
        if (!item.formurl && !hasChildren) return false;
        return true;
      });
  };

  const navData = useMemo(() => filterNavData(rawNavData), [rawNavData]);

  /* ---- App-panel state ---- */
  const [openAppPanel, setOpenAppPanel] = useState<boolean>(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  /* ---- Hover state for flyout menus ---- */
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  /* ---- Active parent detection ---- */
  const checkPathMatch = (formurl: string | undefined): boolean => {
    if (!formurl) return false;
    const path = location.pathname.toLowerCase();
    const target = formurl.toLowerCase();

    if (target === "/" || target === "/dashboard") {
      return path === target;
    }

    return path === target || path.startsWith(target + "/");
  };

  const getInitialActiveIndex = (): number =>
    navData.findIndex((item) => {
      // 1. Check parent url
      if (item.formurl && checkPathMatch(item.formurl)) return true;

      // 2. Check child and subchild urls
      if (item.lstchildform && item.lstchildform.length > 0) {
        return item.lstchildform.some((child: ChildMenuItem) => {
          if (child.formurl && checkPathMatch(child.formurl)) return true;
          if (child.lstsubchildform && child.lstsubchildform.length > 0) {
            return child.lstsubchildform.some(
              (subChild: SubChildMenuItem) => subChild.formurl && checkPathMatch(subChild.formurl)
            );
          }
          return false;
        });
      }

      return false;
    });

  const [active, setActive] = useState<number>(getInitialActiveIndex);

  // Sync active index state when location pathname changes
  useEffect(() => {
    setActive(getInitialActiveIndex());
  }, [location.pathname, navData]);

  /* ---- App launcher: modules (from HelpMenu's session-based source) ---- */
  const [modules, setModules] = useState<AppModule[]>([]);
  const [isLoadingModules, setIsLoadingModules] = useState<boolean>(false);
  const [appSearchTerm, setAppSearchTerm] = useState<string>("");

  // Load modules the first time the panel is opened (mirrors HelpMenu's lazy load)
  useEffect(() => {
    if (!openAppPanel || modules.length > 0) return;

    setIsLoadingModules(true);
    try {
      setModules(loadAvailableModules());
    } finally {
      setIsLoadingModules(false);
    }
  }, [openAppPanel]);

  const filteredModules = useMemo(() => {
    const term = appSearchTerm.trim().toLowerCase();
    if (!term) return modules;
    return modules.filter((module) =>
      module.ModuleName?.toLowerCase().includes(term),
    );
  }, [modules, appSearchTerm]);

  /**
   * ModuleLogoPath comes back as a relative path (e.g.
   * "assets/images/icon/account.svg"). Prefixing with "/" forces it to
   * resolve from the domain root instead of the current route, so it
   * doesn't break when this component renders on a nested URL.
   */
  const resolveModuleIconPath = (path: string): string =>
    path.startsWith("/") ? path : `/${path}`;

  const handleModuleClick = (module: AppModule) => {
    const isDisabled = module.IsAssigned !== 1 || !module.ModuleURL?.trim();
    if (isDisabled) return;

    navigateToModule(
      module.ModuleURL.trim(),
      module.ModuleId,
      module.IsDefault,
      module.CompModuleId,
    );
  };

  const closeAppPanel = () => {
    setOpenAppPanel(false);
    setAppSearchTerm("");
  };

  return (
    <div className={styles.sidebar_container}>
      <div className={styles.sidebar}>

        {/* ── Logo ── */}
        <div className={styles.logoArea}>
          <img src={LogoDark} alt="Zentixs" />
        </div>

        {/* ── Navigation rail ── */}
        <div className={`${styles.sidebar_menu} w-100`}>
          {navData.map((item, index) => {
            const isParentActive = item.lstchildform?.length
              ? item.lstchildform.some(
                  (child: ChildMenuItem) =>
                    location.pathname === child.formurl ||
                    child.lstsubchildform?.some(
                      (subChild) => location.pathname === subChild.formurl
                    )
                )
              : active === index || location.pathname === item.formurl;

            return (
              <div
                key={index}
                className={styles.menuWrapper}
                onMouseEnter={(e) => {
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                    hoverTimeoutRef.current = null;
                  }
                  setHoverIndex(index);
                }}
                onMouseLeave={() => {
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                  }
                  hoverTimeoutRef.current = setTimeout(() => {
                    setHoverIndex(null);
                    hoverTimeoutRef.current = null;
                  }, 200);
                }}
              >
                {/* Primary nav item */}
                {item.lstchildform?.length ? (
                  <div
                    className={`${styles.menuItem} ${isParentActive ? styles.active : ""}`}
                    title={item.formname}
                  >
                    <i className={`${item.iconclass} ${styles.icon}`} />
                    <div className={styles.tooltip}>{item.formname}</div>
                    <i className={`ri-arrow-right-s-line ${styles.arrow}`} />
                  </div>
                ) : (
                  <Link
                    to={item.formurl}
                    className={`${styles.menuItem} ${isParentActive ? styles.active : ""}`}
                    onClick={() => setActive(index)}
                    title={item.formname}
                  >
                    <i className={`${item.iconclass} ${styles.icon}`} />
                    <div className={styles.tooltip}>{item.formname}</div>
                  </Link>
                )}

                {/* ── Flyout submenu ── */}
                {hoverIndex === index && item.lstchildform?.length ? (
                  <div
                    className={styles.subMenu}
                    onMouseEnter={() => {
                      if (hoverTimeoutRef.current) {
                        clearTimeout(hoverTimeoutRef.current);
                        hoverTimeoutRef.current = null;
                      }
                    }}
                  >

                    <div className={styles.subMenuHeader}>
                      {item.iconclass && (
                        <i className={`${item.iconclass} ${styles.subMenuHeaderIcon}`} />
                      )}
                      <span className={styles.subMenuTitle}>{item.formname}</span>
                    </div>

                    <div className={styles.subMenuList}>
                      {item.lstchildform.map((child: ChildMenuItem, childIndex: number) => {
                        const hasSubChildren = child.lstsubchildform && child.lstsubchildform.length > 0;

                        if (hasSubChildren) {
                          return (
                            <div key={childIndex} className={styles.subMenuSection}>
                              {/* Static Section Header Label */}
                              <div className={styles.subMenuSectionHeader}>
                                {child.formname}
                              </div>
                              {/* Clickable Submenu section links */}
                              <div className={styles.subMenuSectionItems}>
                                {child.lstsubchildform?.map((subChild, subChildIndex) => (
                                  <Link
                                    key={subChildIndex}
                                    to={subChild.formurl}
                                    onClick={() => {
                                      setActive(-1);
                                      setHoverIndex(null); // Close sidebar on leaf node selection
                                    }}
                                    className={`${styles.subMenuItem} ${location.pathname === subChild.formurl ? styles.active : ""
                                      }`}
                                  >
                                    {subChild.iconclass && (
                                      <i className={`${subChild.iconclass} ${styles.icon}`} />
                                    )}
                                    <div className={styles.tooltip}>{subChild.formname}</div>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          );
                        } else {
                          // Standard flat clickable link
                          return (
                            <Link
                              key={childIndex}
                              to={child.formurl}
                              onClick={() => {
                                setActive(-1);
                                setHoverIndex(null); // Close sidebar drawer when clicking final leaf url
                              }}
                              className={`${styles.subMenuItem} ${location.pathname === child.formurl ? styles.active : ""
                                }`}
                            >
                              <i className={`${child.iconclass} ${styles.icon}`} />
                              <div className={styles.tooltip}>{child.formname}</div>
                            </Link>
                          );
                        }
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        {/* ── App launcher ── */}
        <div
          className={styles.appLauncher}
          onClick={() => setOpenAppPanel(!openAppPanel)}
          title="App Launcher"
        >
          <i className={openAppPanel ? "ri-close-line" : "ri-apps-2-line"} />
        </div>

        {/* ── Profile dropdown ── */}
        <div className="profilebottom border-top-sidebar">
          <ProfileDropdown />
        </div>

        {/* ── App panel overlay ── */}
        {openAppPanel && (
          <div
            className={styles.appPanelOverlay}
            onClick={closeAppPanel}
          >
            <div
              className={styles.appPanel}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.appPanelTitle}>Applications</div>

              <input
                type="text"
                placeholder="Search apps…"
                className={styles.searchBox}
                value={appSearchTerm}
                onChange={(e) => setAppSearchTerm(e.target.value)}
              />

              <div className={styles.appGrid}>
                {isLoadingModules && (
                  <div
                    className="text-center text-muted py-3"
                    style={{ gridColumn: "1 / -1" }}
                  >
                    Loading...
                  </div>
                )}

                {!isLoadingModules && filteredModules.length === 0 && (
                  <div
                    className="text-center text-muted py-3"
                    style={{ gridColumn: "1 / -1" }}
                  >
                    No modules available
                  </div>
                )}

                {!isLoadingModules &&
                  filteredModules.map((module) => {
                    const isDisabled =
                      module.IsAssigned !== 1 || !module.ModuleURL?.trim();

                    return (
                      <div
                        key={module.ModuleId}
                        className={`${styles.appItem} ${isDisabled ? "opacity-75 pe-none" : ""}`}
                        onClick={() => handleModuleClick(module)}
                      >
                        {module.ModuleLogoPath ? (
                          <img
                            src={resolveModuleIconPath(module.ModuleLogoPath)}
                            alt={module.ModuleName}
                          />
                        ) : (
                          <i className={module.IsDefault === 1 ? "ri-user-3-line" : "ri-stack-line"} />
                        )}
                        <span>{module.ModuleName}</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default VistaSideBar;
